import React, { useEffect, useMemo, useState } from "react"
import { useQuery } from "@apollo/client/react"
import { Common } from "lib/types"
import { CheckLeaf } from "lib/types/workflow"
import {
  GetPaymentProvidersDocument,
  GetPaymentProvidersQuery,
  OrderWorkflowNodeConditionComparisonType as Comparison
} from "lib/types/generated/graphql"
import { getPaymentProviderDisplayName } from "helpers/getPaymentDisplayName"
import { BILLING_ADDRESS_EMAIL } from "helpers/workflowConverter"

import MultiSelect from "components/Ui/Form/MultiSelect"
import {
  FormSection,
  FormLabel,
  SelectWrapper,
  TextArea,
  HelperText,
  ValueListRow,
  ValueInput,
  RemoveValueButton,
  AddValueButton
} from "./NodeEditorSidebar.styled"
import {
  CHECK_TYPE_GROUPS,
  CUSTOM_HTTP,
  MAX_EMAILS,
  ORDER_LINE_PRODUCT_PARENT_ID,
  ORDER_LINE_PRODUCT_VARIANT_ID,
  PAYMENT_PROVIDER_NAME,
  findInvalidEmails,
  getComparisonsForCheckType,
  isValidJson,
  parseEmailList
} from "./conditionFormHelpers"

const getValuePlaceholder = (checkType: string): string => {
  if (checkType === ORDER_LINE_PRODUCT_PARENT_ID) {
    return "e.g. shirt-001"
  }
  if (checkType === ORDER_LINE_PRODUCT_VARIANT_ID) {
    return "e.g. shirt-001-blue-m"
  }
  return ""
}

type Props = {
  value: CheckLeaf
  onChange: (next: CheckLeaf) => void
}

const isMultiValueComparison = (comparison: string): boolean =>
  comparison === Comparison.Contains || comparison === Comparison.NotContains

const LeafCheckEditor = ({ value, onChange }: Props) => {
  const checkType = value.checkType || PAYMENT_PROVIDER_NAME
  const comparison = value.comparison || Comparison.Equals

  const isPaymentProvider = checkType === PAYMENT_PROVIDER_NAME
  const isCustomHttp = checkType === CUSTOM_HTTP
  const isBillingEmail = checkType === BILLING_ADDRESS_EMAIL
  const isMultiValue = isMultiValueComparison(comparison)

  const { data: providersData, loading: loadingProviders } =
    useQuery<GetPaymentProvidersQuery>(GetPaymentProvidersDocument, {
      skip: !isPaymentProvider
    })

  const providerOptions: Common.Option[] = useMemo(() => {
    const providers = providersData?.getPaymentProviders ?? []
    const seen = new Set<string>()
    const options: Common.Option[] = []
    for (const p of providers) {
      if (!seen.has(p.type)) {
        seen.add(p.type)
        options.push({
          value: p.type,
          label: getPaymentProviderDisplayName(p.type)
        })
      }
    }
    return options
  }, [providersData])

  const selectedProviders: Common.Option[] = useMemo(() => {
    if (!isPaymentProvider) return []
    return (value.values ?? []).map((v) => ({
      value: v,
      label: getPaymentProviderDisplayName(v)
    }))
  }, [isPaymentProvider, value.values])

  const [billingEmailsText, setBillingEmailsText] = useState<string>(() =>
    isBillingEmail ? (value.values ?? []).join("\n") : ""
  )

  useEffect(() => {
    if (!isBillingEmail) return
    setBillingEmailsText((current) => {
      const fromValues = (value.values ?? []).join("\n")
      // Keep user-typed text in sync only when it represents a different
      // canonical list — avoids clobbering whitespace/order on every keystroke.
      const currentParsed = parseEmailList(current).join("\n")
      const valuesNormalized = (value.values ?? []).join("\n")
      return currentParsed === valuesNormalized ? current : fromValues
    })
  }, [isBillingEmail, value.values])

  const [multiValues, setMultiValues] = useState<string[]>(() =>
    isMultiValue && (value.values?.length ?? 0) > 0 ? [...value.values] : [""]
  )

  useEffect(() => {
    if (!isMultiValue) return
    setMultiValues((current) => {
      const incoming = value.values ?? []
      if (incoming.length === 0) return current.length === 0 ? [""] : current
      const sameLength = incoming.length === current.filter((v) => v).length
      if (sameLength && incoming.every((v, i) => v === current[i])) return current
      return [...incoming]
    })
  }, [isMultiValue, value.values])

  const comparisonOptions = useMemo(
    () => getComparisonsForCheckType(checkType),
    [checkType]
  )

  useEffect(() => {
    if (isCustomHttp) return
    if (!comparisonOptions.some((c) => c.value === comparison)) {
      onChange({ ...value, comparison: Comparison.Equals })
    }
  }, [comparisonOptions, comparison, value, onChange, isCustomHttp])

  const parsedBillingEmails = useMemo(
    () => (isBillingEmail ? parseEmailList(billingEmailsText) : []),
    [isBillingEmail, billingEmailsText]
  )
  const invalidBillingEmails = useMemo(
    () => findInvalidEmails(parsedBillingEmails),
    [parsedBillingEmails]
  )

  const handleCheckTypeChange = (nextType: string) => {
    const allowed = getComparisonsForCheckType(nextType)
    const nextComparison = allowed.some((c) => c.value === comparison)
      ? comparison
      : Comparison.Equals
    onChange({
      kind: "leaf",
      checkType: nextType,
      comparison: nextType === CUSTOM_HTTP ? "" : nextComparison,
      values: [],
      endpoint: nextType === CUSTOM_HTTP ? (value.endpoint ?? "") : undefined,
      customData: nextType === CUSTOM_HTTP ? (value.customData ?? "") : undefined
    })
    setMultiValues([""])
    setBillingEmailsText("")
  }

  const handleComparisonChange = (next: string) => {
    onChange({ ...value, comparison: next })
  }

  const handleSingleValueChange = (next: string) => {
    onChange({ ...value, values: next ? [next] : [] })
  }

  const handleMultiValuesChange = (next: string[]) => {
    setMultiValues(next)
    onChange({ ...value, values: next.filter((v) => v.trim().length > 0) })
  }

  const handleProvidersChange = (next: Common.Option[]) => {
    onChange({ ...value, values: next.map((p) => p.value) })
  }

  const handleBillingEmailsChange = (raw: string) => {
    setBillingEmailsText(raw)
    onChange({ ...value, values: parseEmailList(raw) })
  }

  const handleEndpointChange = (next: string) => {
    onChange({ ...value, endpoint: next })
  }

  const handleCustomDataChange = (next: string) => {
    onChange({ ...value, customData: next })
  }

  const customDataValid = isValidJson(value.customData ?? "")

  return (
    <>
      <SelectWrapper>
        <FormLabel>Check Type</FormLabel>
        <select
          value={checkType}
          onChange={(e) => handleCheckTypeChange(e.target.value)}
        >
          {CHECK_TYPE_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((ct) => (
                <option key={ct.value} value={ct.value}>
                  {ct.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </SelectWrapper>

      {isBillingEmail ? (
        <>
          <FormSection>
            <FormLabel>Billing address emails (max {MAX_EMAILS})</FormLabel>
            <TextArea
              value={billingEmailsText}
              onChange={(e) => handleBillingEmailsChange(e.target.value)}
              placeholder={"alice@example.com\nbob@example.com\n..."}
              rows={10}
            />
          </FormSection>
          <HelperText>
            <strong>
              {parsedBillingEmails.length} unique email
              {parsedBillingEmails.length === 1 ? "" : "s"}
            </strong>
            Paste or type one email per line (commas, semicolons, and spaces
            also work). The workflow matches if the order&rsquo;s billing
            address email equals any of the listed values.
            {parsedBillingEmails.length > MAX_EMAILS && (
              <>
                <br />
                <span style={{ color: "#cc3333" }}>
                  {parsedBillingEmails.length} emails exceeds the max of{" "}
                  {MAX_EMAILS}. Remove{" "}
                  {parsedBillingEmails.length - MAX_EMAILS} to save.
                </span>
              </>
            )}
            {invalidBillingEmails.length > 0 && (
              <>
                <br />
                <span style={{ color: "#cc3333" }}>
                  Invalid format: {invalidBillingEmails.slice(0, 3).join(", ")}
                  {invalidBillingEmails.length > 3
                    ? ` and ${invalidBillingEmails.length - 3} more`
                    : ""}
                </span>
              </>
            )}
          </HelperText>
        </>
      ) : isCustomHttp ? (
        <>
          <FormSection>
            <FormLabel>Endpoint URL</FormLabel>
            <input
              type="text"
              value={value.endpoint ?? ""}
              onChange={(e) => handleEndpointChange(e.target.value)}
              placeholder="https://api.example.com/check"
            />
            {value.endpoint && !/^https:\/\/.+/.test(value.endpoint) && (
              <span style={{ color: "red", fontSize: "1.1rem" }}>
                Must be a valid HTTPS URL
              </span>
            )}
          </FormSection>
          <FormSection>
            <FormLabel>Request Body (JSON)</FormLabel>
            <TextArea
              value={value.customData ?? ""}
              onChange={(e) => handleCustomDataChange(e.target.value)}
              placeholder={'{\n  "key": "value"\n}'}
            />
            {!customDataValid && (
              <span style={{ color: "red", fontSize: "1.1rem" }}>
                Must be valid JSON
              </span>
            )}
          </FormSection>
          <HelperText>
            <strong>Your endpoint receives a POST with:</strong>
            <pre>{`{
  "workflowId": "1fca8ad7-0905-...",
  "workflowRevision": 0,
  "executionId": "ce24cc5c-b30d-...",
  "orderId": "cca69007-82f8-...",
  "customData": { <your JSON above> }
}`}</pre>
            <strong>Expected response:</strong>
            Return <code>{`{ "match": true }`}</code> → <strong>Yes</strong>{" "}
            branch, or <code>{`{ "match": false }`}</code> →{" "}
            <strong>No</strong> branch. Optionally include{" "}
            <code>&quot;errorMessage&quot;</code> for debugging.
          </HelperText>
        </>
      ) : (
        <>
          <SelectWrapper>
            <FormLabel>Comparison</FormLabel>
            <select
              value={comparison}
              onChange={(e) => handleComparisonChange(e.target.value)}
            >
              {comparisonOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </SelectWrapper>

          {isPaymentProvider ? (
            <FormSection>
              <MultiSelect
                label="Payment Providers"
                options={providerOptions}
                value={selectedProviders}
                setValue={(next: Common.Option[]) =>
                  handleProvidersChange(next)
                }
                placeholder="Select providers..."
                loading={loadingProviders}
              />
            </FormSection>
          ) : isMultiValue ? (
            <FormSection>
              <FormLabel>Values (max 10)</FormLabel>
              {multiValues.map((val, idx) => (
                <ValueListRow key={idx}>
                  <ValueInput
                    type="text"
                    value={val}
                    maxLength={100}
                    placeholder={
                      getValuePlaceholder(checkType) || `Value ${idx + 1}`
                    }
                    onChange={(e) => {
                      const updated = [...multiValues]
                      updated[idx] = e.target.value
                      handleMultiValuesChange(updated)
                    }}
                  />
                  {multiValues.length > 1 && (
                    <RemoveValueButton
                      type="button"
                      onClick={() =>
                        handleMultiValuesChange(
                          multiValues.filter((_, i) => i !== idx)
                        )
                      }
                    >
                      ×
                    </RemoveValueButton>
                  )}
                </ValueListRow>
              ))}
              <AddValueButton
                type="button"
                disabled={multiValues.length >= 10}
                onClick={() =>
                  handleMultiValuesChange([...multiValues, ""])
                }
              >
                + Add value
              </AddValueButton>
            </FormSection>
          ) : (
            <FormSection>
              <FormLabel>Value</FormLabel>
              <ValueInput
                type="text"
                maxLength={100}
                value={value.values?.[0] ?? ""}
                placeholder={getValuePlaceholder(checkType)}
                onChange={(e) => handleSingleValueChange(e.target.value)}
              />
            </FormSection>
          )}
        </>
      )}
    </>
  )
}

export default LeafCheckEditor
